/**
 * Edit dialog for a Studio video: title, description, thumbnail, status.
 */

import {useCallback, useEffect, useState} from 'react'
import {Image, Platform, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {launchImageLibraryAsync} from 'expo-image-picker'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Text} from '#/components/Typography'
import {useStudioUpdateVideo} from '#/state/queries/studio'
import {
  getStudioAuthHeaders,
  type StudioVideoFormat,
  type StudioVideoStatus,
} from '#/lib/api/studio'
import type {DialogControlProps} from '#/components/Dialog'
import type {StudioVideo} from '#/lib/api/studio'

const AWS_S3_BUCKET =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_BUCKET) ||
  'shxrevappblob'
const AWS_S3_REGION =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_REGION) ||
  'us-east-2'
const DEFAULT_PRESIGNED_ENDPOINT =
  'https://shxre.social/api/s3/presigned-url'
const PRESIGNED_URL_ENDPOINT =
  (typeof process !== 'undefined' &&
    process.env?.EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT) ||
  DEFAULT_PRESIGNED_ENDPOINT

function getPresignedEndpoint(): string {
  if (!PRESIGNED_URL_ENDPOINT || !PRESIGNED_URL_ENDPOINT.includes('presigned-url'))
    return DEFAULT_PRESIGNED_ENDPOINT
  try {
    const u = new URL(PRESIGNED_URL_ENDPOINT)
    if (u.hostname === 'share.social') return DEFAULT_PRESIGNED_ENDPOINT
  } catch {
    return DEFAULT_PRESIGNED_ENDPOINT
  }
  return PRESIGNED_URL_ENDPOINT
}

const presignedEndpoint = getPresignedEndpoint()

function getMakePublicEndpoint(): string {
  return presignedEndpoint.replace('/api/s3/presigned-url', '/api/s3/make-public')
}

async function makeS3ObjectPublic(key: string): Promise<void> {
  const res = await fetch(getMakePublicEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
    },
    body: JSON.stringify({key}),
  })
  if (!res.ok) console.warn('Failed to set S3 object public:', key, res.status)
}

type ThumbnailInput =
  | {type: 'file'; file: File}
  | {type: 'uri'; uri: string; fileSize?: number; mimeType?: string}
  | null

async function uploadThumbnailToS3(
  input: ThumbnailInput,
  _: (s: string) => string,
): Promise<string> {
  if (!input) throw new Error('No thumbnail')
  const baseName =
    input.type === 'file'
      ? input.file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      : 'thumb.jpg'
  const key = `studio/thumbnails/${Date.now()}-${baseName}`
  const contentType =
    input.type === 'file'
      ? input.file.type || 'image/jpeg'
      : input.mimeType || 'image/jpeg'
  let fileSize: number
  let body: Blob | File
  if (input.type === 'file') {
    fileSize = input.file.size
    body = input.file
  } else {
    const blob = await (await fetch(input.uri)).blob()
    fileSize = input.fileSize ?? blob.size
    body = blob
  }
  const res = await fetch(presignedEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
    },
    body: JSON.stringify({key, contentType, fileSize}),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as {error?: string}).error || res.statusText)
  }
  const {url} = (await res.json()) as {url: string}
  await fetch(url, {method: 'PUT', headers: {'Content-Type': contentType}, body})
  await makeS3ObjectPublic(key)
  return `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
}

export function StudioVideoEditDialog({
  control,
  video,
  onSuccess,
}: {
  control: DialogControlProps
  video: StudioVideo | null
  onSuccess?: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const updateVideo = useStudioUpdateVideo()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<StudioVideoStatus>('draft')
  const [videoFormat, setVideoFormat] = useState<StudioVideoFormat>('standard')
  const [newThumbnail, setNewThumbnail] = useState<ThumbnailInput>(null)
  const [removeThumbnail, setRemoveThumbnail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isUploadingThumb, setIsUploadingThumb] = useState(false)

  useEffect(() => {
    if (video) {
      setTitle(video.title)
      setDescription(video.description ?? '')
      setStatus((video.status as StudioVideoStatus) || 'draft')
      setVideoFormat(video.video_format ?? 'standard')
      setNewThumbnail(null)
      setRemoveThumbnail(false)
      setError(null)
    }
  }, [video])

  const handleThumbnailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      setNewThumbnail(file ? {type: 'file', file} : null)
      setRemoveThumbnail(false)
      setError(null)
    },
    [],
  )

  const handlePickThumbnailNative = useCallback(async () => {
    try {
      const res = await launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.8,
        selectionLimit: 1,
      })
      const asset = res.assets?.[0]
      if (!asset?.uri) return
      setNewThumbnail({
        type: 'uri',
        uri: asset.uri,
        fileSize: asset.fileSize,
        mimeType: asset.mimeType ?? 'image/jpeg',
      })
      setRemoveThumbnail(false)
      setError(null)
    } catch {
      // cancelled
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!video) return
    setError(null)
    if (!title.trim()) {
      setError(_(msg`Title is required`))
      return
    }
    let thumbnailPath: string | undefined
    if (removeThumbnail) {
      thumbnailPath = ''
    } else if (newThumbnail) {
      try {
        setIsUploadingThumb(true)
        thumbnailPath = await uploadThumbnailToS3(newThumbnail, _)
      } catch (e) {
        setError(
          e instanceof Error ? e.message : _(msg`Failed to upload thumbnail`),
        )
        setIsUploadingThumb(false)
        return
      } finally {
        setIsUploadingThumb(false)
      }
    } else {
      thumbnailPath = undefined
    }
    try {
      await updateVideo.mutateAsync({
        id: video.id,
        input: {
          title: title.trim(),
          description: description.trim() || undefined,
          status,
          video_format: videoFormat,
          ...(thumbnailPath !== undefined && {thumbnail_path: thumbnailPath}),
        },
      })
      control.close(() => {
        onSuccess?.()
      })
    } catch (e) {
      setError(
        e instanceof Error ? e.message : _(msg`Failed to update video`),
      )
    }
  }, [
    video,
    title,
    description,
    status,
    newThumbnail,
    removeThumbnail,
    videoFormat,
    updateVideo,
    control,
    onSuccess,
    _,
  ])

  const handleClose = useCallback(() => {
    control.close()
  }, [control])

  if (!video) return null

  const currentThumb = video.thumbnail_path
  const showThumbPreview = currentThumb && !removeThumbnail && !newThumbnail

  return (
    <Dialog.Outer control={control} onClose={handleClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Edit video`)}>
        <Dialog.Header>
          <Dialog.HeaderText>
            <Trans>Edit video</Trans>
          </Dialog.HeaderText>
        </Dialog.Header>

        <TextField.LabelText>
          <Trans>Title</Trans>
        </TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            label={_(msg`Title`)}
            placeholder={_(msg`Enter title`)}
            value={title}
            onChangeText={setTitle}
          />
        </TextField.Root>

        <TextField.LabelText>
          <Trans>Description</Trans>
        </TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            label={_(msg`Description`)}
            placeholder={_(msg`Optional description`)}
            value={description}
            onChangeText={setDescription}
            multiline
          />
        </TextField.Root>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Video size</Trans>
          </Text>
          <Text style={[a.text_xs, a.pb_sm, t.atoms.text_contrast_medium]}>
            <Trans>Standard = SHXRE Videos. Clips = vertical short-form.</Trans>
          </Text>
          <View style={[a.flex_row, a.gap_sm]}>
            <Button
              label={_(msg`SHXRE Videos`)}
              size="small"
              color={videoFormat === 'standard' ? 'primary' : 'secondary'}
              variant={videoFormat === 'standard' ? 'solid' : 'outline'}
              onPress={() => setVideoFormat('standard')}>
              <ButtonText>
                <Trans>SHXRE Videos</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Clips`)}
              size="small"
              color={videoFormat === 'short' ? 'primary' : 'secondary'}
              variant={videoFormat === 'short' ? 'solid' : 'outline'}
              onPress={() => setVideoFormat('short')}>
              <ButtonText>
                <Trans>Clips</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Thumbnail</Trans>
          </Text>
          {showThumbPreview && (
            <View style={[a.pt_xs, a.pb_sm]}>
              {Platform.OS === 'web' ? (
                <img
                  src={currentThumb}
                  alt=""
                  style={{
                    width: 160,
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
              ) : (
                <Image
                  source={{uri: currentThumb}}
                  style={{width: 160, height: 90, borderRadius: 4}}
                  resizeMode="cover"
                />
              )}
              <Button
                label={_(msg`Remove thumbnail`)}
                size="tiny"
                color="secondary"
                variant="outline"
                onPress={() => setRemoveThumbnail(true)}>
                <ButtonText>
                  <Trans>Remove</Trans>
                </ButtonText>
              </Button>
            </View>
          )}
          {!removeThumbnail && (
            <>
              {Platform.OS === 'web' ? (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  style={{display: 'block', marginTop: 4}}
                />
              ) : (
                <Button
                  label={_(msg`Choose new thumbnail`)}
                  size="small"
                  color="secondary"
                  variant="outline"
                  onPress={handlePickThumbnailNative}>
                  <ButtonText>
                    <Trans>Choose new thumbnail</Trans>
                  </ButtonText>
                </Button>
              )}
              {newThumbnail && (
                <View style={[a.pt_xs, a.flex_row, a.gap_sm, a.align_center]}>
                  {newThumbnail.type === 'file' &&
                    newThumbnail.file.type.startsWith('image/') && (
                      <img
                        src={URL.createObjectURL(newThumbnail.file)}
                        alt=""
                        style={{
                          width: 80,
                          height: 45,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    )}
                  {newThumbnail.type === 'uri' && (
                    <Image
                      source={{uri: newThumbnail.uri}}
                      style={{width: 80, height: 45, borderRadius: 4}}
                      resizeMode="cover"
                    />
                  )}
                  <Button
                    label={_(msg`Clear`)}
                    size="tiny"
                    color="secondary"
                    variant="outline"
                    onPress={() => setNewThumbnail(null)}>
                    <ButtonText>
                      <Trans>Clear</Trans>
                    </ButtonText>
                  </Button>
                </View>
              )}
            </>
          )}
          {removeThumbnail && (
            <Button
              label={_(msg`Keep current thumbnail`)}
              size="tiny"
              color="secondary"
              variant="outline"
              onPress={() => setRemoveThumbnail(false)}>
              <ButtonText>
                <Trans>Keep current</Trans>
              </ButtonText>
            </Button>
          )}
        </View>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Status</Trans>
          </Text>
          <View style={[a.flex_row, a.gap_md]}>
            <Button
              label={_(msg`Draft`)}
              size="small"
              color={status === 'draft' ? 'primary' : 'secondary'}
              variant={status === 'draft' ? 'solid' : 'outline'}
              onPress={() => setStatus('draft')}>
              <ButtonText>
                <Trans>Draft</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Published`)}
              size="small"
              color={status === 'published' ? 'primary' : 'secondary'}
              variant={status === 'published' ? 'solid' : 'outline'}
              onPress={() => setStatus('published')}>
              <ButtonText>
                <Trans>Published</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>

        {error ? (
          <Text
            style={[a.text_sm, a.pt_md, {color: t.palette.negative_400}]}>
            {error}
          </Text>
        ) : null}

        <View style={[a.flex_row, a.gap_md, a.pt_xl]}>
          <Button
            label={_(msg`Cancel`)}
            size="large"
            color="secondary"
            onPress={handleClose}>
            <ButtonText>
              <Trans>Cancel</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Save`)}
            size="large"
            color="primary"
            onPress={handleSubmit}
            disabled={updateVideo.isPending || isUploadingThumb}>
            <ButtonText>
              <Trans>Save</Trans>
            </ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
